import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import QueueOutlinedIcon from "@mui/icons-material/QueueOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { getDashboardMetricInsights, getPlatformLiveDashboard } from "../../../api/client";
import { usePlatformDashboardContext } from "./PlatformDashboardContext";
import { openWorkspaceCallPopupInNewTab } from "../../../utils/openWorkspaceInApp";
import MetricAiInsightsDialog from "../../../components/MetricAiInsightsDialog";
import {
  LiveConversationList,
  LiveKpiCard,
  LiveListCard,
} from "../../Dashboard/dashboardLiveShared";

const POLL_MS_FOCUS = 20_000;
const POLL_MS_HIDDEN = 60_000;

function buildSeriesFromTheme(theme) {
  const p = theme.palette;
  return [p.primary.main, p.secondary?.main ?? p.info.main, p.success.main, p.warning.main];
}

function fmtCount(total, hasMore) {
  const n = Number(total) || 0;
  return `${n}${hasMore ? "+" : ""}`;
}

function listBucket(items, hasMore) {
  return { list: Array.isArray(items) ? items : [], hasMore: Boolean(hasMore) };
}

export default function PlatformDashboardLiveTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("settings");
  const { t: tApp } = useTranslation("app");
  const { t: tChat } = useTranslation("chat");
  const platformCtx = usePlatformDashboardContext();
  const dominioIds = platformCtx?.dominioIds || [];

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pollIntervalMs, setPollIntervalMs] = useState(POLL_MS_FOCUS);
  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = useState("");
  const [metricDialogName, setMetricDialogName] = useState("");
  const [metricDialogLabel, setMetricDialogLabel] = useState("");
  const [metricDialogHuman, setMetricDialogHuman] = useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = useState(null);
  const [metricDialogData, setMetricDialogData] = useState(null);
  const [metricDialogError, setMetricDialogError] = useState(null);

  const series = useMemo(() => buildSeriesFromTheme(theme), [theme]);
  const todayStr = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const loadDashboard = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError("");
      try {
        const res = await getPlatformLiveDashboard(
          dominioIds.length ? { dominio_ids: dominioIds } : {}
        );
        setDashboard(res || null);
        setUpdatedAt(new Date());
        if (!res) {
          setError(
            t("dashboardPage.live.error", {
              defaultValue: "Não foi possível carregar o painel ao vivo.",
            })
          );
        }
      } catch {
        setDashboard(null);
        setError(
          t("dashboardPage.live.error", {
            defaultValue: "Não foi possível carregar o painel ao vivo.",
          })
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dominioIds, t]
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const fn = () => setPollIntervalMs(document.hidden ? POLL_MS_HIDDEN : POLL_MS_FOCUS);
    fn();
    document.addEventListener("visibilitychange", fn);
    return () => document.removeEventListener("visibilitychange", fn);
  }, []);

  useEffect(() => {
    const id = setInterval(() => loadDashboard({ silent: true }), pollIntervalMs);
    return () => clearInterval(id);
  }, [loadDashboard, pollIntervalMs]);

  const openConversation = useCallback((row) => {
    openWorkspaceCallPopupInNewTab(row.bd, row.dominio, row.id);
  }, []);

  const totals = dashboard?.totals || {};
  const lists = dashboard?.lists || {};
  const listHasMore = dashboard?.listHasMore || {};
  const workspaceErrors = (dashboard?.workspaces || []).filter((w) => w.error);

  const queue = useMemo(
    () => listBucket(lists.queue, listHasMore.queue),
    [lists.queue, listHasMore.queue]
  );
  const humanActive = useMemo(
    () => listBucket(lists.human_active, listHasMore.human_active),
    [lists.human_active, listHasMore.human_active]
  );
  const humanPendente = useMemo(
    () => listBucket(lists.human_pendente, listHasMore.human_pendente),
    [lists.human_pendente, listHasMore.human_pendente]
  );
  const humanContatoFuturo = useMemo(
    () => listBucket(lists.human_contato_futuro, listHasMore.human_contato_futuro),
    [lists.human_contato_futuro, listHasMore.human_contato_futuro]
  );
  const botFlow = useMemo(
    () => listBucket(lists.bot_flow, listHasMore.bot_flow),
    [lists.bot_flow, listHasMore.bot_flow]
  );
  const aiFlow = useMemo(
    () => listBucket(lists.ai_flow, listHasMore.ai_flow),
    [lists.ai_flow, listHasMore.ai_flow]
  );
  const unreadQueue = useMemo(
    () => listBucket(lists.unread_queue, listHasMore.unread_queue),
    [lists.unread_queue, listHasMore.unread_queue]
  );

  const liveSnapshotForAi = useMemo(
    () => ({
      filaPendente: totals.queue ?? queue.list.length,
      filaHasMore: queue.hasMore,
      emAtendimentoHumano: totals.human_active ?? humanActive.list.length,
      humanoHasMore: humanActive.hasMore,
      pendenteHumanoAtribuido: humanPendente.list.length,
      pendenteHumanoHasMore: humanPendente.hasMore,
      contatoFuturoHumanoAtribuido: humanContatoFuturo.list.length,
      contatoFuturoHumanoHasMore: humanContatoFuturo.hasMore,
      chatbotAbertos: totals.bot_flow ?? botFlow.list.length,
      chatbotHasMore: botFlow.hasMore,
      agenteIaAbertos: totals.ai_flow ?? aiFlow.list.length,
      agenteHasMore: aiFlow.hasMore,
      operadoresOnline: totals.operators_online ?? 0,
      operadoresTotal: totals.operators_total ?? 0,
      filaNaoLidas: unreadQueue.list.length,
      workspacesComErro: workspaceErrors.length,
      atualizadoEm: updatedAt ? new Date(updatedAt).toISOString() : null,
    }),
    [
      totals,
      queue,
      humanActive,
      humanPendente,
      humanContatoFuturo,
      botFlow,
      aiFlow,
      unreadQueue,
      workspaceErrors.length,
      updatedAt,
    ]
  );

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: { start: todayStr, end: todayStr },
        summaryContext: {
          painelAoVivo: true,
          partnerPortal: true,
          snapshot: cfg.fullSnapshot ?? liveSnapshotForAi,
        },
      };
      return getDashboardMetricInsights("atendimento", payload);
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          t("dashboardPage.live.aiError");
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = useCallback(
    (cfg) => {
      setMetricDialogMetric(cfg.metric);
      setMetricDialogName(cfg.metricName || "");
      setMetricDialogLabel(cfg.metricLabel || "");
      setMetricDialogHuman(cfg.metricHuman || "");
      setMetricDialogBasePayload(cfg);
      setMetricDialogOpen(true);
      setMetricDialogData(null);
      setMetricDialogError(null);
      metricInsightsMutation.mutate({ ...cfg, fullSnapshot: liveSnapshotForAi });
    },
    [metricInsightsMutation, liveSnapshotForAi]
  );

  const timeFmt = updatedAt ? dayjs(updatedAt).format("DD/MM/YYYY HH:mm") : "—";

  const qVal = fmtCount(totals.queue ?? queue.list.length, queue.hasMore);
  const hVal = fmtCount(totals.human_active ?? humanActive.list.length, humanActive.hasMore);
  const onVal = `${totals.operators_online ?? 0}/${totals.operators_total ?? 0}`;
  const autoTotal = Number(totals.bot_flow ?? 0) + Number(totals.ai_flow ?? 0);
  const autoVal = fmtCount(autoTotal, botFlow.hasMore || aiFlow.hasMore);

  const aiOverviewCfg = useMemo(
    () => ({
      metric: "painel_ao_vivo_visao",
      metricName: t("dashboardPage.live.aiOverviewMetricName"),
      metricLabel: t("dashboardPage.tabLive"),
      metricDefinition: t("dashboardPage.live.aiOverviewDefinition"),
      metricValue: liveSnapshotForAi,
      metricHuman: "",
    }),
    [t, liveSnapshotForAi]
  );

  const listFooter = (hasMore) =>
    hasMore ? (
      <Typography variant="caption" color="text.secondary">
        {t("dashboardPage.live.moreNotListed")}
      </Typography>
    ) : null;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t("dashboardPage.live.pollHint", {
              defaultValue: "Os números são atualizados sozinhos a cada 20 segundos",
            })}
          </Typography>
          {pollIntervalMs > POLL_MS_FOCUS ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {t("dashboardPage.live.pollSlowerWhenHidden")}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.disabled">
            {t("dashboardPage.live.lastUpdate", { time: timeFmt, defaultValue: "Atualizado: {{time}}" })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={isMobile ? "stretch" : "flex-end"}>
          <Tooltip title={tChat("analytics.explainAi")}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => openAi(aiOverviewCfg)}
              disabled={loading && !updatedAt}
            >
              {t("dashboardPage.live.aiExplainPanel")}
            </Button>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => loadDashboard()}
            disabled={loading}
          >
            {t("dashboardPage.live.refresh", { defaultValue: "Atualizar" })}
          </Button>
        </Stack>
      </Box>

      {loading && !dashboard ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {workspaceErrors.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tApp("partnerConsole.live.partialErrors", {
            count: workspaceErrors.length,
            defaultValue:
              "{{count}} workspace(s) não responderam — os totais incluem apenas os disponíveis.",
          })}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <LiveKpiCard
          loading={loading && !dashboard}
          icon={<QueueOutlinedIcon />}
          title={t("dashboardPage.live.kpiQueue", { defaultValue: "Na fila" })}
          value={qVal}
          color={series[0]}
          onAi={() =>
            openAi({
              metric: "ao_vivo_fila",
              metricName: t("dashboardPage.live.kpiQueue"),
              metricLabel: t("dashboardPage.live.kpiQueue"),
              metricDefinition: t("dashboardPage.live.aiQueueDefinition"),
              metricValue: { count: totals.queue ?? queue.list.length, hasMore: queue.hasMore },
              metricHuman: qVal,
            })
          }
          tChat={tChat}
        />
        <LiveKpiCard
          loading={loading && !dashboard}
          icon={<SupportAgentIcon />}
          title={t("dashboardPage.live.kpiHumanActive", { defaultValue: "Com operador" })}
          value={hVal}
          color={series[1]}
          onAi={() =>
            openAi({
              metric: "ao_vivo_humano",
              metricName: t("dashboardPage.live.humanTitle"),
              metricLabel: t("dashboardPage.live.kpiHumanActive"),
              metricDefinition: t("dashboardPage.live.aiHumanDefinition"),
              metricValue: {
                count: totals.human_active ?? humanActive.list.length,
                hasMore: humanActive.hasMore,
                amostra: humanActive.list.slice(0, 6).map((x) => ({
                  id: x.id,
                  nome: x.name,
                  workspace: x.dominio,
                })),
              },
              metricHuman: hVal,
            })
          }
          tChat={tChat}
        />
        <LiveKpiCard
          loading={loading && !dashboard}
          icon={<GroupsOutlinedIcon />}
          title={t("dashboardPage.live.kpiOnline", { defaultValue: "Quem está online" })}
          value={onVal}
          color={series[2]}
          sub={t("dashboardPage.live.kpiOnlineSub", {
            online: totals.operators_online ?? 0,
            total: totals.operators_total ?? 0,
            defaultValue: "{{online}} online agora de {{total}} na equipe",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_equipe_online",
              metricName: t("dashboardPage.live.kpiOnline"),
              metricLabel: t("dashboardPage.live.kpiOnline"),
              metricDefinition: t("dashboardPage.live.aiOnlineDefinition"),
              metricValue: {
                online: totals.operators_online ?? 0,
                total: totals.operators_total ?? 0,
              },
              metricHuman: onVal,
            })
          }
          tChat={tChat}
        />
        <LiveKpiCard
          loading={loading && !dashboard}
          icon={<HubOutlinedIcon />}
          title={t("dashboardPage.live.kpiAutomation", { defaultValue: "Automático (chatbot e IA)" })}
          value={autoVal}
          color={series[3]}
          sub={t("dashboardPage.live.kpiAutomationSub", {
            bot: totals.bot_flow ?? botFlow.list.length,
            ai: totals.ai_flow ?? aiFlow.list.length,
            defaultValue: "Chatbot: {{bot}} · IA: {{ai}}",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_automacao",
              metricName: t("dashboardPage.live.kpiAutomation"),
              metricLabel: t("dashboardPage.live.kpiAutomation"),
              metricDefinition: t("dashboardPage.live.aiAutomationDefinition"),
              metricValue: {
                chatbot: totals.bot_flow ?? botFlow.list.length,
                chatbotHasMore: botFlow.hasMore,
                agenteIa: totals.ai_flow ?? aiFlow.list.length,
                agenteHasMore: aiFlow.hasMore,
              },
              metricHuman: autoVal,
            })
          }
          tChat={tChat}
        />
      </Grid>

      {unreadQueue.list.length > 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("dashboardPage.live.unreadQueueBanner", {
            count: unreadQueue.list.length,
            more: unreadQueue.hasMore ? "+" : "",
          })}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <LiveListCard
          title={t("dashboardPage.live.queueTitle", { defaultValue: "Fila (esperando alguém da equipe)" })}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: queue.list.length,
            more: queue.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_fila",
              metricName: t("dashboardPage.live.queueTitle"),
              metricDefinition: t("dashboardPage.live.aiQueueListDefinition"),
              metricValue: queue.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                workspace: c.dominio,
                canal: c.channel || c.channelType,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={listFooter(queue.hasMore)}
        >
          <LiveConversationList
            items={queue.list}
            emptyLabel={t("dashboardPage.live.empty", { defaultValue: "Nada no momento." })}
            onOpen={openConversation}
            t={t}
            showWorkspace
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.humanTitle", { defaultValue: "Atendimento com operador" })}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: humanActive.list.length,
            more: humanActive.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_humano",
              metricName: t("dashboardPage.live.humanTitle"),
              metricDefinition: t("dashboardPage.live.aiHumanListDefinition"),
              metricValue: humanActive.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                workspace: c.dominio,
                operador: c.assignedTo,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={listFooter(humanActive.hasMore)}
        >
          <LiveConversationList
            items={humanActive.list}
            emptyLabel={t("dashboardPage.live.empty", { defaultValue: "Nada no momento." })}
            onOpen={openConversation}
            t={t}
            showWorkspace
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.humanPendenteTitle", {
            defaultValue: "Pendente (operador atribuído)",
          })}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: humanPendente.list.length,
            more: humanPendente.hasMore ? "+" : "",
          })}
          tChat={tChat}
          footer={listFooter(humanPendente.hasMore)}
        >
          <LiveConversationList
            items={humanPendente.list}
            emptyLabel={t("dashboardPage.live.empty", { defaultValue: "Nada no momento." })}
            onOpen={openConversation}
            t={t}
            showWorkspace
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.humanContatoFuturoTitle", {
            defaultValue: "Contato futuro (operador)",
          })}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: humanContatoFuturo.list.length,
            more: humanContatoFuturo.hasMore ? "+" : "",
          })}
          tChat={tChat}
          footer={listFooter(humanContatoFuturo.hasMore)}
        >
          <LiveConversationList
            items={humanContatoFuturo.list}
            emptyLabel={t("dashboardPage.live.empty", { defaultValue: "Nada no momento." })}
            onOpen={openConversation}
            t={t}
            showWorkspace
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.botTitle", { defaultValue: "Chatbot" })}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: botFlow.list.length,
            more: botFlow.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_bot",
              metricName: t("dashboardPage.live.botTitle"),
              metricDefinition: t("dashboardPage.live.aiBotListDefinition"),
              metricValue: botFlow.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                workspace: c.dominio,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={listFooter(botFlow.hasMore)}
        >
          <LiveConversationList
            items={botFlow.list}
            emptyLabel={t("dashboardPage.live.empty", { defaultValue: "Nada no momento." })}
            onOpen={openConversation}
            t={t}
            showWorkspace
          />
        </LiveListCard>

        <LiveListCard
          title={t("dashboardPage.live.aiTitle", { defaultValue: "Agentes de IA" })}
          sampleHint={t("dashboardPage.live.listVirtualizedSample", {
            count: aiFlow.list.length,
            more: aiFlow.hasMore ? "+" : "",
          })}
          onAi={() =>
            openAi({
              metric: "ao_vivo_lista_ia",
              metricName: t("dashboardPage.live.aiTitle"),
              metricDefinition: t("dashboardPage.live.aiAgentListDefinition"),
              metricValue: aiFlow.list.slice(0, 15).map((c) => ({
                id: c.id,
                cliente: c.name,
                workspace: c.dominio,
                agente: c.agentName,
              })),
              metricHuman: "",
            })
          }
          tChat={tChat}
          footer={listFooter(aiFlow.hasMore)}
        >
          <LiveConversationList
            items={aiFlow.list}
            emptyLabel={t("dashboardPage.live.empty", { defaultValue: "Nada no momento." })}
            onOpen={openConversation}
            t={t}
            showWorkspace
          />
        </LiveListCard>
      </Grid>

      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start: todayStr, end: todayStr }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="atendimento"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Box>
  );
}
